<template>
  <div
    class="min-h-screen p-4 sm:p-6 lg:p-8 font-sans flex flex-col items-center justify-center relative overflow-hidden bg-slate-50 dark:bg-[#0B1120] text-slate-900 dark:text-white"
  >
    <!-- Ambient Background Mesh/Glow -->
    <div class="fixed inset-0 pointer-events-none overflow-hidden">
      <div
        class="absolute -top-32 -left-32 w-80 h-80 sm:w-96 sm:h-96 bg-indigo-500/10 dark:bg-indigo-600/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute -bottom-32 -right-32 w-80 h-80 sm:w-96 sm:h-96 bg-purple-500/10 dark:bg-purple-600/10 rounded-full blur-3xl"
      ></div>
    </div>

    <div
      class="bg-white/95 dark:bg-[#111C30]/95 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-10 border border-slate-200/90 dark:border-[#263449] shadow-2xl w-full max-w-md text-center relative z-10 transform transition-all duration-300"
    >
      <div class="mb-6">
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800/40 mb-3"
        >
          <Scan class="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
          Attendance Scan
        </span>
        <h2
          class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight"
        >
          Verification Result
        </h2>
      </div>

      <!-- Loading State -->
      <transition
        name="fade-scale"
        mode="out-in"
      >
        <div
          v-if="isLoading"
          key="loading"
          class="py-6 flex items-center justify-center"
        >
          <AppLoader
            variant="card"
            size="md"
            message="Processing Attendance..."
            subtext="Verifying scanned QR payload with institutional records"
            color="indigo"
          />
        </div>

        <!-- Result State -->
        <div
          v-else-if="popupMessage"
          key="result"
          class="space-y-6"
        >
          <div class="flex justify-center">
            <transition
              name="icon-bounce"
              appear
            >
              <div
                :key="popupType"
                :class="{
                  'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20': popupType === 'success',
                  'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20': popupType === 'error',
                  'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20': popupType === 'info',
                }"
                class="w-20 h-20 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-lg transform scale-0 animate-scale-in-icon"
              >
                <CheckCircle
                  v-if="popupType === 'success'"
                  class="w-10 h-10 animate-pulse-icon"
                />
                <XCircle
                  v-else-if="popupType === 'error'"
                  class="w-10 h-10 animate-shake-icon"
                />
                <AlertCircle
                  v-else-if="popupType === 'info'"
                  class="w-10 h-10 animate-bounce-icon"
                />
              </div>
            </transition>
          </div>

          <div>
            <h3
              :class="[
                'text-lg sm:text-xl font-black tracking-tight mb-1.5',
                popupType === 'success'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : popupType === 'error'
                  ? 'text-rose-600 dark:text-rose-400'
                  : 'text-indigo-600 dark:text-indigo-400',
              ]"
            >
              {{ popupTitle }}
            </h3>
            <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              {{ popupMessage }}
            </p>
          </div>

          <div
            v-if="popupDetails.type || popupDetails.name"
            class="bg-slate-50 dark:bg-[#18243A] border border-slate-200/80 dark:border-[#263449] rounded-2xl p-4 text-left space-y-2 shadow-xs"
          >
            <p
              v-if="popupDetails.type"
              class="text-xs text-slate-700 dark:text-slate-300 font-medium flex items-center justify-between"
            >
              <span class="text-slate-400 dark:text-slate-500 font-normal">Role / Type</span>
              <span class="font-bold text-slate-900 dark:text-white capitalize">{{ popupDetails.type }}</span>
            </p>
            <p
              v-if="popupDetails.name"
              class="text-xs text-slate-700 dark:text-slate-300 font-medium flex items-center justify-between"
            >
              <span class="text-slate-400 dark:text-slate-500 font-normal">Account Name</span>
              <span class="font-bold text-slate-900 dark:text-white">{{ popupDetails.name }}</span>
            </p>
          </div>

          <button
            @click="goBackToScanner"
            class="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all font-semibold shadow-md shadow-indigo-600/20 active:scale-98 text-sm"
          >
            <Scan class="w-4 h-4" />
            <span>Scan Another</span>
          </button>
        </div>

        <!-- No Result State -->
        <div
          v-else
          key="no-result"
          class="py-6 space-y-6"
        >
          <div class="flex justify-center">
            <div
              class="w-20 h-20 bg-slate-100 dark:bg-[#18243A] rounded-2xl sm:rounded-3xl flex items-center justify-center border border-slate-200 dark:border-[#263449]"
            >
              <Scan class="w-10 h-10 text-slate-400" />
            </div>
          </div>
          <div>
            <h3 class="text-base font-bold text-slate-900 dark:text-white mb-1">
              No Scan Result
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              Please point your camera or scanner at a valid QR token.
            </p>
          </div>
          <button
            @click="goBackToScanner"
            class="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all font-semibold shadow-md shadow-indigo-600/20 active:scale-98 text-sm"
          >
            <Scan class="w-4 h-4" />
            <span>Open Scanner</span>
          </button>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed } from "vue";
  import { useStore } from "vuex";
  import { useRouter } from "vue-router";
  import { CheckCircle, XCircle, Scan, AlertCircle } from "lucide-vue-next"; // Import AlertCircle

  const store = useStore();
  const router = useRouter();

  const isLoading = ref(true);
  const popupMessage = ref("");
  const popupType = ref(""); // 'success', 'error', or 'info'
  const popupDetails = ref({}); // To store type and name from backend response
  onMounted(async () => {
    isLoading.value = true;
    try {
      const result = await store.dispatch("scanAttendance");
      if (result && result.message) {
        if (
          result.message === "checkin successful" ||
          result.message === "checkout successful"
        ) {
          popupType.value = "success";
        } else {
          popupType.value = "info";
        }
        popupMessage.value = result.message;
        popupDetails.value = { type: result.type, name: result.name };

        // ✅ Redirect after 5 seconds
        setTimeout(() => {
          router.replace("/");
        }, 5000);
      } else {
        popupType.value = "error";
        popupMessage.value = "Unknown response from backend.";
        popupDetails.value = {};

        setTimeout(() => {
          router.replace("/");
        }, 5000);
      }
    } catch (error) {
      console.error("Error during attendance scan:", error);

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        const errorMessage = error.response.data.message;

        if (errorMessage === "Already checked in and out today.") {
          popupType.value = "info";
        } else {
          popupType.value = "error";
        }

        popupMessage.value = errorMessage;
      } else {
        popupType.value = "error";
        popupMessage.value =
          error.message || "Attendance scan failed. Please try again.";
      }

      popupDetails.value = {};

      // ✅ Redirect after 5 seconds on error
      setTimeout(() => {
        router.replace("/");
      }, 5000);
    } finally {
      isLoading.value = false;
    }
  });

  const goBackToScanner = () => {
    router.replace("/"); // Navigate back to the scanner component
  };

  const popupTitle = computed(() => {
    if (popupType.value === "success") return "Success!";
    if (popupType.value === "error") return "Failed!";
    return "Info";
  });
</script>

<style scoped>
  /* Background Blobs Animation */
  @keyframes blob-1 {
    0%,
    100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(30px, -50px) scale(1.1);
    }
    66% {
      transform: translate(-20px, 20px) scale(0.9);
    }
  }
  @keyframes blob-2 {
    0%,
    100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(-40px, 60px) scale(1.2);
    }
    66% {
      transform: translate(50px, -30px) scale(0.8);
    }
  }
  @keyframes blob-3 {
    0%,
    100% {
      transform: translate(0, 0) scale(1);
    }
    33% {
      transform: translate(20px, 40px) scale(0.95);
    }
    66% {
      transform: translate(-60px, -20px) scale(1.1);
    }
  }

  .animate-blob-1 {
    animation: blob-1 10s infinite alternate ease-in-out;
  }
  .animate-blob-2 {
    animation: blob-2 12s infinite alternate-reverse ease-in-out;
  }
  .animate-blob-3 {
    animation: blob-3 11s infinite alternate ease-in-out;
  }

  /* Loading Spinner */
  @keyframes spin-slow {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .animate-spin-slow {
    animation: spin-slow 1.5s linear infinite;
  }

  /* Icon Animations */
  @keyframes scale-in-icon {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    80% {
      transform: scale(1.1);
      opacity: 1;
    }
    100% {
      transform: scale(1);
    }
  }
  @keyframes pulse-icon {
    0%,
    100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.8;
    }
  }
  @keyframes shake-icon {
    0%,
    100% {
      transform: translateX(0);
    }
    20%,
    60% {
      transform: translateX(-8px);
    }
    40%,
    80% {
      transform: translateX(8px);
    }
  }
  @keyframes bounce-icon {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-15px);
    }
    60% {
      transform: translateY(-7px);
    }
  }

  .animate-scale-in-icon {
    animation: scale-in-icon 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)
      forwards;
  }
  .animate-pulse-icon {
    animation: pulse-icon 2s infinite ease-in-out;
  }
  .animate-shake-icon {
    animation: shake-icon 0.6s ease-in-out;
  }
  .animate-bounce-icon {
    animation: bounce-icon 1s ease-in-out;
  }

  /* Vue Transitions */
  .fade-scale-enter-active,
  .fade-scale-leave-active {
    transition: all 0.4s ease-in-out;
  }
  .fade-scale-enter-from,
  .fade-scale-leave-to {
    opacity: 0;
    transform: scale(0.95);
  }

  .icon-bounce-enter-active {
    transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
  .icon-bounce-enter-from {
    opacity: 0;
    transform: scale(0.5);
  }
</style>
